/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { ErrorNames, HttpResponseCodes } from '../../enums';

import type { XhrHttpRequestInit } from '../../services/http-request';

const getParamValueFromUrl = (paramName: string): string | null => new URLSearchParams(window.location.search).get(paramName);

const addOriginToUrl = (url: string): string => {
    if (url.indexOf('://') === -1) {
        url = `${window.location.origin}${url[0] === '/' ? url : `/${url}`}`;
    }

    return url;
};

const addParamsToQuery = (url = '/', params: string[][] | Record<string, string> | string | URLSearchParams | null | void): string => {
    if (
        !params
        || (params && ((params as string[][]).length === 0 || Object.keys(params).length === 0))
    ) {
        return url;
    }

    const [path, query = '']: string[] = url.split('?');
    const currentQueryParams = new URLSearchParams(query);

    new URLSearchParams(params).forEach((value: string, key: string): void => {
        currentQueryParams.set(key, value);
    });

    const currentQueryParamsStr = currentQueryParams.toString();

    return `${path}${currentQueryParamsStr ? `?${currentQueryParamsStr}` : ''}`;
};

const addActionsParamsToQuery = (url: string, params: Record<string, string[]>) => {
    const preparedParams = Object.entries(params).map(([key, value]) => [key, value.join(',')]);

    return addParamsToQuery(url, preparedParams);
};

const queryParamsToObj = (queryStr: string): Record<string, string> => Object.fromEntries(queryStr.split('&').map(paramStr => paramStr.split('=')));

const downloadBlob = (blob: Blob, filename: string): void => {
    const downloadUrl: string = window.URL.createObjectURL(blob);

    downloadFromUrl(downloadUrl, filename);
    setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 100); // cleanup
};

const downloadFromUrl = (linkToFile: string, filename = ''): void => {
    const element: HTMLAnchorElement = document.createElement('a');

    element.setAttribute('href', linkToFile);
    element.setAttribute('download', filename);
    element.click();
};

const addBaseUrl = (url: string, baseUrl: string): string => {
    if (url.indexOf('://') === -1 && baseUrl) {
        url = (baseUrl + url).replace('//', '/');
    }

    return url;
};

const createResponse = (status: HttpResponseCodes, response: any, url: string, options?: XhrHttpRequestInit | RequestInit): Response => ({
    ok: status >= HttpResponseCodes.OK && status <= 299,
    status,
    headers: new Headers(options?.headers as Headers ?? {}),
    redirected: false,
    json: (): Promise<any> => Promise.resolve(response),
    statusText: HttpResponseCodes[status],
    type: 'default',
    url,
    clone: () => ({} as Response),
    body: null,
    bodyUsed: false,
    arrayBuffer: (): Promise<ArrayBuffer> => Promise.resolve(new ArrayBuffer(0)),
    formData: (): Promise<FormData> => Promise.resolve(new FormData()),
    text: (): Promise<string> => Promise.resolve(''),
    blob: (): Promise<Blob> => Promise.resolve(new Blob()),
});

const isFailedFetchError = (errorText: string): boolean => errorText === 'Failed to fetch';

const isErrorThatNeedToBeShown = (err: Error | any): boolean => {
    const ignoredErrorsInProduction = [
        ErrorNames.AbortError,
        ErrorNames.AuthInternalError,
    ];

    if (
        err instanceof Error
        && (ignoredErrorsInProduction.includes(err.name as ErrorNames) || isFailedFetchError(err.message))
    ) {
        if (process.env.NODE_ENV === 'development') {
            console.error('[DEV_MODE_MESSAGE]', err);
        }
        return false;
    }

    return true;
};

export {
    getParamValueFromUrl,
    addOriginToUrl,
    addParamsToQuery,
    queryParamsToObj,
    downloadBlob,
    downloadFromUrl,
    addActionsParamsToQuery,
    addBaseUrl,
    createResponse,
    isFailedFetchError,
    isErrorThatNeedToBeShown,
};
